import { useState, useEffect } from 'react';
import { 
  Play, 
  Pause, 
  SkipBack, 
  SkipForward, 
  Type, 
  Music, 
  Layers, 
  Scissors, 
  Plus, 
  Download, 
  Share2, 
  MoreHorizontal, 
  Sparkles,
  Search,
  Clock,
  Trash2,
  Settings2,
  Volume2,
  Maximize2,
  Filter,
  Film,
  Image as ImageIcon,
  ChevronRight,
  ChevronDown,
  LayoutTemplate
} from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { ImageWithFallback } from './figma/ImageWithFallback';
import { cn } from './ui/utils';

interface MediaClip {
  id: string;
  type: 'video' | 'image' | 'audio';
  thumbnail: string;
  duration: string;
  label: string;
}

export function VideoAdStudioPage() {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [activeTab, setActiveTab] = useState<'media' | 'text' | 'audio' | 'effects'>('media');
  const [selectedAssetId, setSelectedAssetId] = useState<string | null>(null);
  const [selectedLayerId, setSelectedLayerId] = useState<string | null>(null);
  
  const totalDuration = 15; // 15 seconds for a vertical ad

  const library: MediaClip[] = [
    { id: '1', type: 'video', thumbnail: 'https://images.unsplash.com/photo-1517319084147-208a3caceb22?w=400&q=80', duration: '0:15', label: 'Lifestyle Vlog' },
    { id: '2', type: 'video', thumbnail: 'https://images.unsplash.com/photo-1666728451779-85c0bc53d2ef?w=400&q=80', duration: '0:12', label: 'Urban Edit' },
    { id: '3', type: 'image', thumbnail: 'https://images.unsplash.com/photo-1557804506-669a67965ba0?w=400&q=80', duration: '0:05', label: 'Studio Shot' },
    { id: '4', type: 'video', thumbnail: 'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=400&q=80', duration: '0:08', label: 'Team Collab' },
    { id: '5', type: 'image', thumbnail: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=400&q=80', duration: '0:05', label: 'Portrait' },
    { id: '6', type: 'video', thumbnail: 'https://images.unsplash.com/photo-1517841905240-472988babdf9?w=400&q=80', duration: '0:10', label: 'Fashion Reel' },
  ];

  const timelineLayers = [
    { id: 'l1', type: 'video', label: 'Main Video', start: 0, width: '60%', clip: library[0] },
    { id: 'l2', type: 'video', label: 'B-Roll', start: 60, width: '40%', clip: library[1] },
    { id: 'l3', type: 'text', label: 'Title Hook', start: 10, width: '30%', text: 'THE FUTURE OF HIKE' },
    { id: 'l4', type: 'audio', label: 'Background Music', start: 0, width: '100%', audio: 'Upbeat Urban Mix' },
  ];

  // Simple play/pause simulation
  useEffect(() => {
    let interval: any;
    if (isPlaying) {
      interval = setInterval(() => {
        setCurrentTime((prev) => {
          if (prev >= totalDuration) {
            setIsPlaying(false);
            return 0;
          }
          return prev + 0.1;
        });
      }, 100);
    } else {
      clearInterval(interval);
    }
    return () => clearInterval(interval);
  }, [isPlaying]);

  const formatTime = (time: number) => {
    const mins = Math.floor(time / 60);
    const secs = Math.floor(time % 60);
    const ms = Math.floor((time % 1) * 10);
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}.${ms}`;
  };

  return (
    <div className="h-screen bg-[#f8f9fa] p-2.5 overflow-hidden">
      <div className="h-full flex flex-col rounded-2xl bg-white border border-gray-200/80 shadow-sm overflow-hidden">
      {/* Top Header */}
      <header className="h-16 border-b px-6 flex items-center justify-between z-10 shrink-0">
        <div className="flex items-center gap-4">
          <div className="w-10 h-10 bg-purple-600 rounded-xl flex items-center justify-center shadow-lg shadow-purple-200">
            <Film className="w-5 h-5 text-white" />
          </div>
          <div>
            <div className="flex items-center gap-2 mb-0.5">
              <h1 className="text-sm font-bold text-[#101828]">Urban Hike Q4 Campaign Ad</h1>
              <Badge variant="secondary" className="bg-purple-50 text-purple-600 border-0 text-[10px] h-4">DRAFT</Badge>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-[10px] text-gray-500 font-medium">9:16 Vertical</span>
              <span className="text-[10px] text-gray-300">•</span>
              <span className="text-[10px] text-gray-500 font-medium italic">1080x1920</span>
              <span className="text-[10px] text-gray-300">•</span>
              <span className="text-[10px] text-gray-400">Auto-saved 2m ago</span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" className="text-gray-600 font-semibold hover:bg-gray-50 rounded-xl">
            <Share2 className="w-4 h-4 mr-2" /> Share
          </Button>
          <div className="h-6 w-[1px] bg-gray-200 mx-1" />
          <Button size="sm" className="bg-purple-600 hover:bg-purple-700 text-white rounded-xl px-6 font-bold shadow-md shadow-purple-100 transition-all active:scale-95">
            <Download className="w-4 h-4 mr-2" /> Export
          </Button>
        </div>
      </header>

      <div className="flex flex-1 overflow-hidden">
        {/* Left Toolbar / Asset Library */}
        <aside className="w-[340px] bg-white border-r flex flex-col overflow-hidden shrink-0">
          <div className="p-4 border-b flex gap-1 bg-gray-50/50">
            {(['media', 'text', 'audio', 'effects'] as const).map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={cn(
                  "flex-1 py-2 text-[10px] font-black uppercase tracking-widest rounded-lg transition-all",
                  activeTab === tab ? "bg-white text-purple-600 shadow-sm border border-gray-100" : "text-gray-400 hover:text-gray-600"
                )}
              >
                {tab}
              </button>
            ))}
          </div>

          <div className="flex-1 overflow-y-auto p-4 custom-scrollbar">
            {activeTab === 'media' && (
              <>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-[11px] font-black text-gray-900 uppercase tracking-tighter">Project Assets</h3>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" className="h-7 w-7 rounded-lg text-gray-400">
                      <Filter className="w-3.5 h-3.5" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7 rounded-lg bg-purple-50 text-purple-600">
                      <Plus className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
                
                <div className="relative mb-6">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                  <input 
                    placeholder="Search library..." 
                    className="w-full bg-gray-50 border border-gray-100 rounded-xl pl-9 pr-4 py-2.5 text-xs focus:outline-none focus:ring-2 focus:ring-purple-500/10"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  {library.map((item) => (
                    <div 
                      key={item.id} 
                      className={cn(
                        "group cursor-pointer relative",
                        selectedAssetId === item.id && "ring-2 ring-purple-600 ring-offset-2 rounded-xl"
                      )}
                      onClick={() => setSelectedAssetId(item.id)}
                    >
                      <div className="aspect-[3/4] rounded-xl overflow-hidden border border-gray-100 bg-gray-100 shadow-sm transition-all group-hover:scale-[1.02]">
                        <ImageWithFallback src={item.thumbnail} alt={item.label} className="w-full h-full object-cover" />
                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                          <Plus className="w-6 h-6 text-white" />
                        </div>
                        <div className="absolute top-2 left-2 bg-black/60 rounded-md p-1 backdrop-blur-sm">
                           {item.type === 'video' ? <Film className="w-3 h-3 text-white" /> : <ImageIcon className="w-3 h-3 text-white" />}
                        </div>
                        <div className="absolute bottom-1.5 right-1.5 px-1.5 bg-black/60 rounded-md text-[9px] text-white font-mono backdrop-blur-sm">
                          {item.duration}
                        </div>
                      </div>
                      <p className="text-[10px] font-bold text-gray-600 mt-2 truncate px-1">{item.label}</p>
                    </div>
                  ))}
                </div>
              </>
            )}

            {activeTab === 'text' && (
              <div className="space-y-4">
                 <h3 className="text-[11px] font-black text-gray-900 uppercase tracking-tighter">Text Presets</h3>
                 <div className="grid grid-cols-1 gap-3">
                    {['Headline Bold', 'Subtitle Light', 'Call to Action', 'Social Handle', 'Subtitles/Captions'].map((preset) => (
                      <div key={preset} className="p-4 border border-dashed border-gray-200 rounded-xl hover:border-purple-300 hover:bg-purple-50/30 cursor-pointer transition-all">
                        <p className="text-xs font-bold text-gray-900 mb-1">{preset}</p>
                        <p className="text-[10px] text-gray-500 italic">Drag to timeline</p>
                      </div>
                    ))}
                 </div>
              </div>
            )}
            
            {activeTab === 'audio' && (
              <div className="space-y-4">
                 <h3 className="text-[11px] font-black text-gray-900 uppercase tracking-tighter">Audio Library</h3>
                 <div className="space-y-2">
                    {['Upbeat Urban Mix', 'Corporate Chill', 'Dynamic Vlog', 'Lo-fi Beats'].map((track) => (
                      <div key={track} className="p-3 bg-gray-50 rounded-xl border border-gray-100 flex items-center justify-between group cursor-pointer hover:bg-white hover:border-purple-200 transition-all">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                            <Music className="w-4 h-4 text-purple-600" />
                          </div>
                          <div>
                            <p className="text-[10px] font-bold text-gray-900">{track}</p>
                            <p className="text-[9px] text-gray-500">2:45 • Royalty Free</p>
                          </div>
                        </div>
                        <Button variant="ghost" size="icon" className="h-7 w-7 rounded-lg text-gray-400 group-hover:text-purple-600">
                          <Plus className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                 </div>
              </div>
            )}
          </div>

          <div className="p-4 border-t bg-gray-50/80">
            <div className="bg-white p-3 rounded-2xl border border-gray-100 shadow-sm flex items-center gap-3 mb-3">
               <div className="w-9 h-9 bg-purple-100 rounded-xl flex items-center justify-center shrink-0">
                  <Sparkles className="w-4 h-4 text-purple-600" />
               </div>
               <div className="flex-1 min-w-0">
                  <p className="text-[11px] font-bold text-gray-900">AI Script Builder</p>
                  <p className="text-[9px] text-gray-500 truncate">Generate high-conversion hooks</p>
               </div>
               <ChevronRight className="w-3 h-3 text-gray-400" />
            </div>
            <Button variant="outline" className="w-full rounded-xl text-[11px] font-bold text-purple-600 border-purple-100 bg-white hover:bg-purple-50 h-10">
              Storage: 8.2GB / 20GB
            </Button>
          </div>
        </aside>

        {/* Main Editor View */}
        <main className="flex-1 flex flex-col bg-[#f0f1f3] overflow-hidden relative">
          <div className="flex-1 flex items-center justify-center p-8 overflow-hidden relative">
            {/* Top Editor Actions */}
            <div className="absolute top-6 left-1/2 -translate-x-1/2 flex items-center gap-4 bg-white/90 backdrop-blur-xl px-5 py-2.5 rounded-2xl border border-white/40 shadow-xl shadow-black/5 z-20">
               <button className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-500 transition-colors" title="Layouts"><LayoutTemplate className="w-4 h-4" /></button>
               <div className="h-4 w-[1px] bg-gray-200" />
               <button className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-500 transition-colors" title="Layers"><Layers className="w-4 h-4" /></button>
               <div className="h-4 w-[1px] bg-gray-200" />
               <button className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-500 transition-colors" title="Settings"><Settings2 className="w-4 h-4" /></button>
               <div className="h-4 w-[1px] bg-gray-200" />
               <button className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-500 transition-colors" title="Fullscreen"><Maximize2 className="w-4 h-4" /></button>
            </div>

            {/* Vertical Ad Preview (Phone Frame Mockup) */}
            <div className="relative w-[320px] aspect-[9/16] bg-black rounded-[48px] shadow-[0_40px_100px_-20px_rgba(0,0,0,0.4)] ring-12 ring-[#1c1c1e] overflow-hidden group">
              {/* Active Clip Preview */}
              <div className="w-full h-full relative">
                <ImageWithFallback 
                  src={currentTime < 9 ? library[0].thumbnail : library[1].thumbnail} 
                  className="w-full h-full object-cover brightness-[0.9] transition-all duration-300" 
                />
                
                {/* HUD Elements Overlay */}
                <div className="absolute top-12 left-6 right-6 pointer-events-none">
                   <div className="flex items-center gap-2 mb-6 animate-in fade-in slide-in-from-top-4 duration-700">
                      <ImageWithFallback src="https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=100" className="w-8 h-8 rounded-full border-2 border-white/20" />
                      <div>
                        <p className="text-[10px] font-black text-white shadow-sm tracking-tight uppercase">@urban_hike</p>
                        <p className="text-[8px] text-white/70 shadow-sm font-bold">Brand Ambassador</p>
                      </div>
                   </div>
                   
                   {/* Animated Title Text Overlay Mockup */}
                   {currentTime > 1 && currentTime < 8 && (
                      <div className="space-y-1 animate-in zoom-in-95 fade-in duration-500">
                        <h2 className="text-3xl font-black text-white leading-[0.85] drop-shadow-2xl uppercase tracking-tighter italic">
                          THE BEST <br/> VIEW IN <br/> THE CITY
                        </h2>
                        <div className="h-0.5 w-12 bg-purple-500 shadow-lg" />
                      </div>
                   )}
                </div>

                <div className="absolute bottom-12 left-6 right-6 animate-in slide-in-from-bottom-4 duration-700">
                  <Button className="w-full h-12 bg-white hover:bg-gray-50 text-black font-black uppercase text-xs tracking-widest rounded-full shadow-2xl transition-all active:scale-95 group">
                     SHOP NEW COLLECTION
                     <ChevronRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </div>
              </div>

              {/* Central Player Overlay Controls */}
              <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-10">
                 <button onClick={() => setCurrentTime(Math.max(0, currentTime - 5))} className="text-white hover:scale-125 transition-transform"><SkipBack className="w-8 h-8 fill-white/10" /></button>
                 <button 
                  onClick={() => setIsPlaying(!isPlaying)}
                  className="w-20 h-20 bg-white/20 backdrop-blur-xl rounded-full flex items-center justify-center border border-white/30 hover:scale-110 transition-all shadow-2xl"
                 >
                   {isPlaying ? <Pause className="w-10 h-10 text-white fill-white" /> : <Play className="w-10 h-10 text-white fill-white ml-1.5" />}
                 </button>
                 <button onClick={() => setCurrentTime(Math.min(totalDuration, currentTime + 5))} className="text-white hover:scale-125 transition-transform"><SkipForward className="w-8 h-8 fill-white/10" /></button>
              </div>

              {/* In-Preview Progress Bar */}
              <div className="absolute bottom-0 left-0 right-0 h-1.5 bg-white/10">
                 <div 
                   className="h-full bg-[#be43ff] transition-all duration-100 shadow-[0_0_10px_#be43ff]" 
                   style={{ width: `${(currentTime / totalDuration) * 100}%` }}
                 />
              </div>
            </div>
          </div>

          {/* Timeline Section */}
          <div className="h-[280px] bg-white border-t flex flex-col shadow-[0_-10px_30px_-15px_rgba(0,0,0,0.05)] z-20 shrink-0">
            <div className="flex items-center justify-between px-6 py-3 border-b bg-gray-50/30">
              <div className="flex items-center gap-6">
                 <div className="flex items-center gap-2.5 px-3 py-1.5 bg-white border border-gray-100 rounded-xl shadow-sm">
                    <Clock className="w-3.5 h-3.5 text-purple-600" />
                    <span className="text-[11px] font-mono font-black text-gray-900 leading-none">
                      {formatTime(currentTime)} <span className="text-gray-400 font-medium">/ {formatTime(totalDuration)}</span>
                    </span>
                 </div>
                 <div className="flex items-center gap-1">
                    <button onClick={() => { setCurrentTime(0); setIsPlaying(false); }} className="p-2 hover:bg-gray-100 rounded-xl transition-colors text-gray-500"><SkipBack className="w-4 h-4" /></button>
                    <button 
                      onClick={() => setIsPlaying(!isPlaying)}
                      className="w-10 h-10 bg-[#101828] rounded-full flex items-center justify-center hover:scale-105 transition-all shadow-lg active:scale-95 mx-1"
                    >
                      {isPlaying ? <Pause className="w-4 h-4 text-white fill-white" /> : <Play className="w-4 h-4 text-white fill-white ml-0.5" />}
                    </button>
                    <button onClick={() => { setCurrentTime(totalDuration); setIsPlaying(false); }} className="p-2 hover:bg-gray-100 rounded-xl transition-colors text-gray-500"><SkipForward className="w-4 h-4" /></button>
                 </div>
              </div>

              <div className="flex items-center gap-2">
                 <div className="flex bg-gray-100 rounded-xl p-0.5 mr-2">
                    <Button variant="ghost" size="sm" className="h-8 text-[10px] font-bold px-3 bg-white shadow-sm rounded-lg">Zoom 50%</Button>
                    <Button variant="ghost" size="sm" className="h-8 text-[10px] font-bold px-3 text-gray-500">Fit</Button>
                 </div>
                 <Button variant="ghost" size="sm" className="h-9 text-[11px] font-bold text-gray-600 hover:bg-gray-50 rounded-xl border border-gray-100 bg-white"><Scissors className="w-3.5 h-3.5 mr-2" /> Split Clip</Button>
                 <Button variant="ghost" size="sm" className="h-9 text-[11px] font-bold text-gray-600 hover:bg-gray-50 rounded-xl"><Trash2 className="w-3.5 h-3.5 mr-2" /> Delete</Button>
                 <div className="h-4 w-[1px] bg-gray-200 mx-1.5" />
                 <Button variant="ghost" size="sm" className="h-9 text-[11px] font-bold text-gray-600 hover:bg-gray-50 rounded-xl"><Volume2 className="w-3.5 h-3.5 mr-2" /> Audio 80%</Button>
              </div>
            </div>

            {/* Timeline Tracks Area */}
            <div className="flex-1 overflow-x-auto p-6 relative custom-scrollbar bg-white">
              <div className="space-y-4">
                {/* Visual Tracks */}
                <div className="flex items-center gap-4">
                  <div className="w-20 shrink-0 text-[9px] font-black text-gray-400 uppercase tracking-widest text-right">Visual</div>
                  <div className="relative flex-1 h-16 bg-gray-50 rounded-xl border border-gray-100 overflow-hidden flex items-center">
                    {timelineLayers.filter(l => l.type === 'video').map((layer) => (
                      <div 
                        key={layer.id}
                        style={{ width: layer.width, left: `${layer.start}%` }} 
                        className={cn(
                          "h-full border-r border-white/50 relative group cursor-pointer transition-all",
                          selectedLayerId === layer.id ? "ring-2 ring-purple-600 z-10" : ""
                        )}
                        onClick={() => setSelectedLayerId(layer.id)}
                      >
                        <ImageWithFallback src={layer.clip?.thumbnail || ''} className="w-full h-full object-cover brightness-[0.7] group-hover:brightness-[0.9] transition-all" />
                        <div className="absolute inset-0 flex items-center px-3">
                           <span className="text-[10px] font-bold text-white shadow-sm truncate">{layer.label}</span>
                        </div>
                        <div className="absolute top-1 right-1 px-1 bg-black/40 rounded text-[7px] text-white font-mono">{layer.clip?.duration}</div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Text Tracks */}
                <div className="flex items-center gap-4">
                  <div className="w-20 shrink-0 text-[9px] font-black text-gray-400 uppercase tracking-widest text-right">Titles</div>
                  <div className="relative flex-1 h-10 bg-gray-50 rounded-xl border border-gray-100">
                    {timelineLayers.filter(l => l.type === 'text').map((layer) => (
                      <div 
                        key={layer.id}
                        style={{ width: layer.width, left: `${layer.start}%` }} 
                        className={cn(
                          "absolute inset-y-0 h-full bg-purple-100/50 border border-purple-200 rounded-lg flex items-center px-3 cursor-pointer hover:bg-purple-200 transition-all",
                          selectedLayerId === layer.id ? "ring-2 ring-purple-600 ring-offset-1 z-10 bg-purple-200" : ""
                        )}
                        onClick={() => setSelectedLayerId(layer.id)}
                      >
                        <Type className="w-3 h-3 text-purple-600 mr-2 shrink-0" />
                        <span className="text-[10px] font-bold text-purple-700 truncate">{layer.text}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Audio Tracks */}
                <div className="flex items-center gap-4">
                  <div className="w-20 shrink-0 text-[9px] font-black text-gray-400 uppercase tracking-widest text-right">Audio</div>
                  <div className="relative flex-1 h-10 bg-gray-50 rounded-xl border border-gray-100 overflow-hidden">
                    {timelineLayers.filter(l => l.type === 'audio').map((layer) => (
                      <div 
                        key={layer.id}
                        style={{ width: layer.width, left: `${layer.start}%` }} 
                        className={cn(
                          "absolute inset-y-0 h-full bg-blue-50 border border-blue-100 rounded-lg flex items-center px-3 cursor-pointer hover:bg-blue-100 transition-all",
                          selectedLayerId === layer.id ? "ring-2 ring-blue-600 ring-offset-1 z-10 bg-blue-100" : ""
                        )}
                        onClick={() => setSelectedLayerId(layer.id)}
                      >
                        <Music className="w-3 h-3 text-blue-600 mr-2 shrink-0" />
                        <span className="text-[10px] font-bold text-blue-700 truncate">{layer.audio}</span>
                        {/* Waveform Visualization Mockup */}
                        <div className="ml-auto flex items-end gap-0.5 h-5 opacity-40">
                           {[1,3,2,5,4,2,3,4,6,4,3,2,1,2,4,5,4,3,2,1,3,4,2].map((h, i) => (
                             <div key={i} className="w-0.5 bg-blue-600" style={{ height: `${h * 15}%` }} />
                           ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Central Playhead Pointer (Draggable Marker) */}
              <div 
                className="absolute top-0 bottom-0 w-0.5 bg-purple-600 z-30 transition-all duration-100 pointer-events-none"
                style={{ left: `calc(96px + (100% - 120px) * ${currentTime / totalDuration})` }}
              >
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-4 h-4 bg-purple-600 rounded-full shadow-lg border-2 border-white" />
                <div className="absolute -top-6 left-1/2 -translate-x-1/2 bg-purple-600 text-white text-[9px] font-mono font-bold px-1.5 py-0.5 rounded shadow-md">
                   {currentTime.toFixed(1)}s
                </div>
              </div>
            </div>
          </div>
        </main>

        {/* Right Properties Panel */}
        <aside className="w-[300px] bg-white border-l flex flex-col overflow-hidden shrink-0">
          <div className="p-4 border-b flex items-center justify-between">
            <h3 className="text-xs font-black text-gray-900 uppercase tracking-widest">Clip Properties</h3>
            <Button variant="ghost" size="icon" className="h-6 w-6 text-gray-400">
              <MoreHorizontal className="w-4 h-4" />
            </Button>
          </div>

          <div className="flex-1 overflow-y-auto p-5 space-y-8 custom-scrollbar">
            {selectedLayerId ? (
              <>
                <div className="space-y-4">
                  <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Basic Settings</p>
                  <div className="space-y-3">
                    <div className="space-y-1.5">
                      <label className="text-[11px] font-bold text-gray-700">Clip Label</label>
                      <input 
                        type="text" 
                        defaultValue={timelineLayers.find(l => l.id === selectedLayerId)?.label || ''} 
                        className="w-full bg-gray-50 border border-gray-100 rounded-xl px-3 py-2 text-xs focus:ring-2 focus:ring-purple-500/20 focus:outline-none" 
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1.5">
                        <label className="text-[11px] font-bold text-gray-700">Start Time</label>
                        <div className="flex bg-gray-50 border border-gray-100 rounded-xl px-3 py-2 text-xs">
                           <span>0.0s</span>
                        </div>
                      </div>
                      <div className="space-y-1.5">
                        <label className="text-[11px] font-bold text-gray-700">Duration</label>
                        <div className="flex bg-gray-50 border border-gray-100 rounded-xl px-3 py-2 text-xs">
                           <span>9.0s</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Audio & Effects</p>
                  <div className="space-y-4">
                    <div className="space-y-2">
                       <div className="flex justify-between items-center">
                          <label className="text-[11px] font-bold text-gray-700">Volume</label>
                          <span className="text-[10px] font-mono text-purple-600 font-bold">80%</span>
                       </div>
                       <div className="h-1.5 bg-gray-100 rounded-full relative">
                          <div className="absolute left-0 top-0 bottom-0 w-[80%] bg-purple-500 rounded-full" />
                          <div className="absolute left-[80%] top-1/2 -translate-y-1/2 w-3.5 h-3.5 bg-white border-2 border-purple-500 rounded-full shadow-md cursor-pointer" />
                       </div>
                    </div>
                    <div className="space-y-2">
                       <label className="text-[11px] font-bold text-gray-700">Filters</label>
                       <div className="grid grid-cols-3 gap-2">
                          {['None', 'Vibrant', 'Muted', 'Cinematic', 'Noir', 'Warm'].map((f) => (
                            <button key={f} className={cn(
                              "text-[9px] font-bold py-2 rounded-lg border transition-all",
                              f === 'None' ? "bg-purple-50 border-purple-100 text-purple-600" : "bg-white border-gray-100 text-gray-500 hover:border-purple-200"
                            )}>{f}</button>
                          ))}
                       </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Transitions</p>
                  <div className="p-3 bg-gray-50 rounded-xl border border-gray-100 flex items-center justify-between cursor-pointer hover:bg-white transition-all">
                     <span className="text-xs font-bold text-gray-900">Fade In / Out</span>
                     <ChevronDown className="w-4 h-4 text-gray-400" />
                  </div>
                </div>
              </>
            ) : (
              <div className="h-full flex flex-col items-center justify-center text-center space-y-4 py-20 opacity-40 grayscale">
                 <div className="w-16 h-16 bg-gray-100 rounded-3xl flex items-center justify-center">
                    <Scissors className="w-8 h-8 text-gray-400" />
                 </div>
                 <div>
                    <p className="text-xs font-bold text-gray-900">No Layer Selected</p>
                    <p className="text-[10px] text-gray-500 max-w-[160px] mx-auto mt-1">Select a clip on the timeline to edit its properties.</p>
                 </div>
              </div>
            )}
          </div>
          
          <div className="p-4 border-t bg-gray-50/50">
             <Button variant="ghost" className="w-full h-10 text-[11px] font-bold text-red-500 hover:bg-red-50 hover:text-red-600 rounded-xl">
                <Trash2 className="w-4 h-4 mr-2" /> Remove Clip
             </Button>
          </div>
        </aside>
      </div>

      <style>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 4px;
          height: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #e2e8f0;
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #cbd5e1;
        }
      `}</style>
      </div>
    </div>
  );
}
